import { useContext, useEffect, useMemo, useState } from 'react';

import { AuthContext } from 'context/AuthContext';
import { useUsername } from 'utils/authUtils';
import './User.scss';
import { Option } from 'features/common-elements';
import toast from 'react-hot-toast';
import { Avatar, Button } from 'ui-kit';

import { useMutation, useQuery } from '@apollo/client';

import {
  UserManagementUpdateUserInfo,
  UserManagementUpdateUserInfoVariables,
} from 'mutation/__generated__/UserManagementUpdateUserInfo';
import { UPDATE_USER_INFO } from 'mutation/userData';

import {
  GetUserPermissions,
  GetUserPermissionsVariables,
} from 'query/__generated__/GetUserPermissions';
import { GET_USER_COMPANY } from 'query/company';
import { GET_USER_PERMISSIONS } from 'query/permissionsQueries';

export interface Address {
  address1: string;
  suite: string;
  city: string;
  state: string;
  zipcode: string;
}

// TODO: Address and Title will be added later
export interface UserModel {
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
  title?: string;
  company?: string;
  address?: Address;
  roles: string;
}

const roleOptions: Option[] = [
  { value: 'Analyst', label: 'Analyst' },
  { value: 'CFO', label: 'CFO' },
  { value: 'CLO', label: 'CLO' },
  { value: 'Trader', label: 'Trader' },
  { value: 'Treasurer', label: 'Treasurer' },
  { value: 'Other', label: 'Other' },
];

const User = () => {
  const { state: authState } = useContext(AuthContext);
  const username = useUsername();
  const [userInfo, setUserInfo] = useState<UserModel>({
    givenName: '',
    familyName: '',
    email: '',
    phone: '',
    title: '',
    company: authState.company?.name || '',
    address: {
      address1: '',
      suite: '',
      city: '',
      state: '',
      zipcode: '',
    },
    roles: 'Other',
  });
  const [isEditing, setIsEditing] = useState(false);

  useQuery<GetUserPermissions, GetUserPermissionsVariables>(GET_USER_PERMISSIONS, {
    variables: { requestData: { username: username || '' } },
    skip: !username,
    onCompleted: (data) => {
      setUserInfo((prevInfo) => ({
        ...prevInfo,
        userID: data.userManagementGetUser.user.userID,
        givenName: data.userManagementGetUser.user.givenName || '',
        familyName: data.userManagementGetUser.user.familyName || '',
        email: data.userManagementGetUser.user.email || '',
        phone: data.userManagementGetUser.user.phoneNumber || '',
        roles: data.userManagementGetUser.permissions?.[0]?.roles?.join(', ') || '',
        lastLoginDate: data.userManagementGetUser.user.lastLoginDate || '',
      }));
    },
  });

  const getUserPermissionsVariables: GetUserPermissionsVariables = useMemo(
    () => ({
      requestData: {
        username: username || '',
      },
    }),
    [username],
  );
  const [updateUserInfo] = useMutation<
    UserManagementUpdateUserInfo,
    UserManagementUpdateUserInfoVariables
  >(UPDATE_USER_INFO, {
    refetchQueries: [
      {
        query: GET_USER_COMPANY,
      },
      {
        query: GET_USER_PERMISSIONS,
        variables: getUserPermissionsVariables,
      },
    ],
    onCompleted: () => {
      setIsEditing(false);
      toast.success('User information updated successfully!');
    },
    onError: (_error) => {
      toast.error('Failed to update user information');
    },
  });

  // Update company name when it changes in auth context
  useEffect(() => {
    if (authState.company) {
      setUserInfo((prevInfo) => ({
        ...prevInfo,
        company: authState.company?.name || '',
      }));
    }
  }, [authState.company]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // if (name.startsWith('address.')) {
    // const addressField = name.split('.')[1] as keyof Address;
    // setUserInfo((prevInfo) => ({
    //   ...prevInfo,
    //   address: {
    //     ...prevInfo.address,
    //     [addressField]: value,
    //   },
    // }));
    // } else {
    setUserInfo((prevInfo) => ({ ...prevInfo, [name]: value }));
    // }
  };

  const handleSave = async () => {
    // setError('');

    if (!username) {
      toast.error('Failed to get user information');
      return;
    }

    if (!userInfo.email) {
      toast.error('Email is required to update user information');
      return;
    }

    updateUserInfo({
      variables: {
        requestData: {
          // address: JSON.stringify(userInfo.address),
          username,
          userEmail: userInfo.email, // Required for Cognito operations
          givenName: userInfo.givenName,
          familyName: userInfo.familyName,
          // title: userInfo.title,
        },
      },
    });
  };

  const getFieldValue = (name: string): string => {
    return (userInfo[name as keyof UserModel] as string) || '';
  };

  const renderInputField = (
    label: string,
    name: keyof UserModel | string,
    type = 'text',
    disabled = false,
  ) => (
    <div className="mb-[16px] w-full">
      <label className="mb-1 block text-left text-xs font-bold capitalize">{label}</label>
      {type === 'select' ? (
        <select
          name={name}
          value={getFieldValue(name)}
          onChange={handleInputChange}
          className={`color-[#E0E0E0] radius-[4px] w-full rounded-[4px] border-[1px] border-[#616161] bg-[transparent] px-[12px] py-[2px] text-xs
            ${disabled ? 'pointer-events-none cursor-not-allowed border-[#d0d0d0] text-[#a0a0a0]' : ''}`}
          disabled={disabled}
        >
          {roleOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={getFieldValue(name)}
          onChange={handleInputChange}
          className={`color-[#E0E0E0] radius-[4px] w-full rounded-[4px] border-[1px] border-[#E0E0E0] bg-[transparent] px-[15px] py-[5px] text-sm
            ${disabled ? 'pointer-events-none cursor-not-allowed border-[#d0d0d0] text-[#a0a0a0]' : ''}`}
          disabled={disabled}
        />
      )}
    </div>
  );

  const renderInfoRow = (label: string, value: string | undefined) => (
    <div className="items-left mb-4 mt-0 flex">
      <div className="flex flex-row items-center">
        <span className="block w-[100px] text-left text-xs font-bold uppercase text-slate-200">
          {label}
        </span>
        <span className="mw-[220px] block text-sm text-white">{value}</span>
      </div>
    </div>
  );

  return (
    <div className="flex items-start gap-4 rounded-md p-4 text-white">
      <div className="mb-4 flex items-center">
        <Avatar name={userInfo.givenName} width="88px" height="88px" fontSize="32px" />
      </div>
      {isEditing ? (
        <form>
          <div className="flex gap-5">
            <div className="flex w-[70%] gap-3">
              {renderInputField('First Name', 'givenName', 'text')}
              {renderInputField('Last Name', 'familyName', 'text')}
            </div>
            {/* <div>
              {renderInputField('Role', 'role', 'select')}
            </div> */}
          </div>
          {/* <div className='w-[70%]'>
            {renderInputField('Title', 'title')}
            </div> */}
          <div className="w-[70%]">{renderInputField('Company', 'company', 'text', true)}</div>
          <div className="w-[70%]">{renderInputField('Email', 'email', 'email', true)}</div>
          <div className="w-[70%]">{renderInputField('Phone', 'phone', 'text', true)}</div>
          {/* <div className='flex w-[70%] gap-3'>
            {renderInputField('Address1', 'address.address1')}
            {renderInputField('Suite/Office', 'address.suite')}
          </div>
          <div className='flex w-[70%] gap-3'>
            {renderInputField('City', 'address.city')}
            {renderInputField('State', 'address.state')}
          </div>
          <div className='w-[70%]'>
            {renderInputField('Zipcode', 'address.zipcode')}
          </div> */}
          <div className="flex justify-start gap-4">
            <Button label="Save Info" type="primary" onClick={handleSave} className="mt-4">
              Save info
            </Button>
            <Button
              label="Cancel"
              type="secondary"
              onClick={() => setIsEditing(false)}
              className="mt-4"
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col space-y-4">
          <div className="items-left mb-4 mt-0 flex items-center">
            <div className="flex flex-row items-center">
              <span className="block w-[100px] text-left text-xs font-bold uppercase text-slate-200">
                Name
              </span>
              <span className="mw-[220px] block text-xl font-bold text-white">
                {`${userInfo.givenName} ${userInfo.familyName}`.trim()}
              </span>
            </div>
            <div className="ml-[25px] flex flex-row items-center">
              <span className="block text-xs font-bold uppercase text-slate-200">Roles</span>
              <span className="ml-[10px] block rounded-[3px] bg-[#D8DDFD] px-[4px] py-[3px] text-sm text-info-default">
                {userInfo.roles}
              </span>
            </div>
          </div>
          {/* {renderInfoRow('Title', userInfo.title)} */}
          {renderInfoRow('Company', userInfo.company)}
          {renderInfoRow('Email', userInfo.email)}
          {renderInfoRow('Phone', userInfo.phone)}
          {/* <div className='flex items-left mb-4 mt-0'>
            <div className='flex flex-row items-center'>
              <p className='flex h-full text-sm font-bold text-slate-200 uppercase w-[100px] text-left items-start'>Address</p>
              <div className='flex flex-col text-sm text-white text-left'>
                {
                  userInfo.address && (
                    <>
                      <span>{`${userInfo.address.address1 ? userInfo.address.address1 + ', ' : ''} ${userInfo.address.suite}`}</span>
                      <span>{`${userInfo.address.city ? userInfo.address.city + ', ' : ''} ${userInfo.address.state}`}</span>
                      <span>{userInfo.address.zipcode}</span>
                    </>
                  )
                }
              </div>
            </div>
          </div> */}
          <div className="flex justify-start">
            <Button label="Edit" type="primary" onClick={() => setIsEditing(true)} className="mt-4">
              Edit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default User;
